import banner from "../../../courses/module-1/img5.png";

const modulo5 = {
  courseId: "finanzas-personales",
  id: "modulo-5",
  title: "📘 MODULE 5: Introduction to Investing",
  estimatedDuration: "2 hours",
  format: "Reading + written simulation + assessment",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objectives",
      title: "🎯 Module objectives",
      content: [
        "Understand what investing really means beyond the myths.",
        "Discover how compound interest multiplies your money over time.",
        "Learn about safe and accessible investment options for young beginners.",
        "Apply the principles of risk, return, and diversification consciously.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Main reading",
      title: "📖 Main reading: What does it mean to invest?",
      content: [
        {
          type: "paragraph",
          text: "Investing means using part of your money to put it to work, with the expectation that it will grow over time. It is not about becoming a millionaire overnight, but about making smart decisions that generate future returns.",
        },
        {
          type: "paragraph",
          text: "“Saving means keeping. Investing means growing.”",
        },
      ],
    },
    {
      id: "comparacion",
      navLabel: "Saving vs Investing",
      title: "💬 How is it different from saving?",
      content: [
        {
          type: "table",
          headers: ["Saving", "Investing"],
          rows: [
            ["Protects money", "Grows money"],
            ["Low or no risk", "Involves some risk"],
            ["Immediate liquidity", "Requires time to see results"],
            ["Ideal for the short term", "Ideal for the medium/long term"],
          ],
        },
      ],
    },
    {
      id: "interes-compuesto",
      navLabel: "Compound Interest",
      title: "💸 The Law of Compound Interest: your financial superpower",
      content: [
        {
          type: "paragraph",
          text: "“Compound interest is the eighth wonder of the world.” Albert Einstein.",
        },
        {
          type: "paragraph",
          text: "📌 What is it?",
        },
        {
          type: "paragraph",
          text: "It is when your money generates interest, and that interest also generates more interest. The longer you let your money grow, the more exponential the result becomes.",
        },
        {
          type: "paragraph",
          text: "🎯 Simple example:",
        },
        {
          type: "paragraph",
          text: "If you invest $1,000 with a 10% annual return:",
        },
        {
          type: "list",
          items: [
            "Year 1 → $1,100",
            "Year 2 → $1,210",
            "Year 3 → $1,331",
            "Year 10 → $2,594",
            "Year 20 → $6,727",
          ],
        },
        {
          type: "paragraph",
          text: "The key is not having a lot of money, but starting early and being consistent.",
        },
      ],
    },
    {
      id: "riesgo-rentabilidad",
      navLabel: "Risk and return",
      title: "⚖ The Law of Risk and Return",
      content: [
        {
          type: "paragraph",
          text: "Every investment has a direct relationship between risk and expected return:",
        },
        {
          type: "table",
          headers: ["Investment type", "Risk", "Potential return", "Example"],
          rows: [
            ["Savings account", "Very low", "Low", "Traditional bank"],
            ["CETES", "Low", "Moderate", "Mexican government"],
            ["Investment funds", "Medium", "Moderate to high", "FIBRAs, ETFs"],
            [
              "Cryptocurrencies/stocks",
              "High",
              "High",
              "Stock market, Bitcoin",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "The higher the return, the higher the risk. And vice versa.",
        },
      ],
    },
    {
      id: "reducir-riesgo",
      navLabel: "Reduce risk",
      title: "📌 How can you reduce risk?",
      content: [
        {
          type: "list",
          items: [
            "Invest only money you do not need in the short term",
            "Diversify: do not put all your eggs in one basket",
            "Get informed before taking action",
            "Start with safer options before looking for high returns",
          ],
        },
      ],
    },
    {
      id: "regla-110",
      navLabel: "Rule of 110",
      title:
        "🔢 Extra: The Rule of 110. How much should you invest based on your age?",
      content: [
        {
          type: "paragraph",
          text: "In addition to understanding compound interest and the risk-return relationship, there is a simple formula to estimate what percentage of your money could go into higher-risk investments.",
        },
        {
          type: "paragraph",
          text: "What does the Rule of 110 say?",
        },
        {
          type: "paragraph",
          text: "110 – your age = recommended percentage for more volatile instruments",
        },
        {
          type: "paragraph",
          text: "👇 Practical example:",
        },
        {
          type: "list",
          items: ["If you are 20 years old:"],
        },
        "110 – 20 = 90",
        "→ up to 90% of your portfolio could go into riskier assets, such as stocks, crypto, or ETFs.",
        {
          type: "list",
          items: ["If you are 30 years old:"],
        },
        "110 – 30 = 80",
        "→ a profile with 80% in riskier assets and 20% in safer investments.",
        {
          type: "paragraph",
          text: "It is not a strict rule, but a useful guide to create a balanced strategy according to your age.",
        },
      ],
    },
    {
      id: "ejercicio",
      navLabel: "Practical exercise",
      title: "🧠 Practical exercise: growth simulation",
      content: [
        {
          type: "paragraph",
          text: "Imagine you can set aside $200 pesos per month to invest in CETES with a 3-month term and an average annual rate of 9%.",
        },
        {
          type: "paragraph",
          text: "How much would you have in 1, 3, and 5 years if you reinvest everything you earn each time?",
        },
        {
          type: "paragraph",
          text: "Use the compound interest formula:",
        },
        {
          type: "list",
          items: [
            "Final amount = Investment × (1 + annual rate / 12)^(12 × years)",
          ],
        },
        {
          type: "paragraph",
          text: "Written exercise:",
        },
        "Calculate and write down how much your money would grow in 3 scenarios:",
        {
          type: "list",
          items: [
            "Investing for 1 year",
            "Investing for 3 years",
            "Investing for 5 years",
          ],
        },
        "Reflect:",
        {
          type: "list",
          items: [
            "Are you more motivated by saving or by seeing your money grow?",
            "What level of risk are you willing to take based on your age?",
          ],
        },
      ],
    },
    {
      id: "ejemplo-real",
      navLabel: "Real example",
      title: "💬 Real young example",
      content: [
        {
          type: "paragraph",
          text: "🎓 Isaac, 21, a student in Querétaro, started investing $500 pesos per month in CETES when he was 19. Today, two years later, he has accumulated more than $13,000 without making major sacrifices.",
        },
        {
          type: "paragraph",
          text: "He did it from his phone, without complications, with consistency and patience.",
        },
        {
          type: "paragraph",
          text: "“Investing made me feel that I have control over my future, even if I start little by little.”",
        },
      ],
    },
    {
      id: "quiz",
      navLabel: "Quiz",
      title: "📝 Self-assessment miniquiz",
      questions: [
        {
          id: "q1",
          question: "What is the main difference between saving and investing?",
          options: [
            { key: "a", text: "Investing is safer" },
            { key: "b", text: "Investing seeks to grow money" },
            { key: "c", text: "Saving is illegal if you have a card" },
            { key: "d", text: "Saving always loses value" },
          ],
          correct: "b",
        },
        {
          id: "q2",
          question: "What is compound interest?",
          options: [
            { key: "a", text: "When you earn more by spending" },
            { key: "b", text: "When interest generates more interest" },
            { key: "c", text: "A tax from the SAT" },
            { key: "d", text: "A banking technique to lose money" },
          ],
          correct: "b",
        },
        {
          id: "q3",
          question: "What does the Law of Risk and Return represent?",
          options: [
            { key: "a", text: "The higher the return, the higher the risk" },
            { key: "b", text: "All investments are safe" },
            { key: "c", text: "You always earn the same" },
            { key: "d", text: "Saving has no risk" },
          ],
          correct: "a",
        },
        {
          id: "q4",
          question: "What can you do to reduce risk?",
          options: [
            { key: "a", text: "Diversify and start with safer options" },
            { key: "b", text: "Bet everything on crypto" },
            { key: "c", text: "Spend before investing" },
            { key: "d", text: "Ignore contracts" },
          ],
          correct: "a",
        },
        {
          id: "q5",
          question: "What is the advantage of starting to invest young?",
          options: [
            { key: "a", text: "More time for your money to grow" },
            { key: "b", text: "Not paying taxes" },
            { key: "c", text: "Having more cards" },
            { key: "d", text: "Less student debt" },
          ],
          correct: "a",
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Recommended bibliography",
      title: "📚 Recommended bibliography",
      content: [
        "Sofía Macías – ‘Pequeño Cerdo Capitalista: Inversiones’",
        "CONDUSEF – ‘Basic investment guide’",
        "Blog ‘El Arte de Invertir Joven’ – educacionfinancieramx.org.mx",
      ],
    },
    {
      id: "frase",
      navLabel: "Quote of the day",
      title: "🧠 Quote of the day",
      content: [
        "The best time to invest was yesterday. The second-best time is today.",
      ],
    },
  ],
};

export default modulo5;
